(() => {
  if (customElements.get('vr-size-guide')) return;
  const canonical = value => String(value || '').trim().toUpperCase()
    .replace(/^XXXXL$/, '4XL').replace(/^XXXL$/, '3XL').replace(/^XXL$/, '2XL')
    .replace(/\s*1\/2$/, ',5').replace('½', ',5').replace('.', ',');
  const bounds = value => String(value).split(/[–-]/).map(Number);
  const high = value => Math.max(...bounds(value));
  const displaySize = (value, kind) => kind === 'shoes' ? canonical(value).replace(/^(\d+),5$/, '$1½') : String(value || '').trim();

  class VrSizeGuide extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.tables = JSON.parse(this.querySelector('[data-vr-size-data]').textContent);
      this.words = JSON.parse(this.querySelector('[data-vr-size-labels]').textContent);
      this.variants = JSON.parse(this.querySelector('[data-vr-size-variants]').textContent);
      this.sizes = this.dataset.sizes.split('|').filter(Boolean);
      this.sizePosition = Number(this.dataset.sizePosition);
      this.kind = this.dataset.kind;
      this.footOnly = this.dataset.footOnly === 'true';
      this.unit = 'cm';
      this.addEventListener('click', event => {
        if (!(event.target instanceof Element)) return;
        const action = event.target.closest('[data-vr-action]');
        if (action) {
          event.preventDefault();
          this.action(action.dataset.vrAction);
        }
        const unit = event.target.closest('[data-vr-unit-choice]');
        if (unit) {
          event.preventDefault();
          this.convert(unit.dataset.vrUnitChoice);
        }
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        if (this.opener?.isConnected) this.opener.focus();
      }, options);
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.recommend();
      }, options);
      this.querySelector('[data-vr-piece]')?.addEventListener('change', event => {
        this.kind = event.target.value;
        this.form.elements.first.value = '';
        this.form.elements.second.value = '';
        this.configure();
      }, options);
      const picker = this.getPicker();
      picker?.addEventListener('variant:update', () => this.updateApplyButton(), options);
      this.configure();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.ready = false;
    }

    getPicker() {
      const adjacent = this.previousElementSibling;
      if (adjacent?.matches('variant-picker') && adjacent.dataset.productId === this.dataset.productId) return adjacent;
      return this.closest('.shopify-section')?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    }

    configure() {
      this.querySelectorAll('[data-vr-table]').forEach(element => { element.hidden = element.dataset.vrTable !== this.kind; });
      this.querySelectorAll('[data-vr-instructions]').forEach(element => { element.hidden = element.dataset.vrInstructions !== this.kind; });
      this.form.elements.usual.replaceChildren(new Option(this.words.choose, ''));
      const usualSizes = [...this.sizes];
      if (this.kind === 'shoes') usualSizes.sort((a, b) => Number(canonical(a).replace(',', '.')) - Number(canonical(b).replace(',', '.')));
      usualSizes.forEach(size => this.form.elements.usual.add(new Option(displaySize(size, this.kind), size)));
      const body = this.querySelector(`[data-vr-table="${this.kind}"] tbody`);
      body.querySelectorAll('[data-vr-unmeasured]').forEach(row => row.remove());
      Array.from(body.rows).forEach(row => {
        const actualSize = this.sizes.find(size => canonical(size) === canonical(row.cells[0].textContent));
        row.hidden = !actualSize;
        if (actualSize) row.cells[0].textContent = displaySize(actualSize, this.kind);
      });
      this.sizes.filter(size => !this.tables[this.kind].some(row => canonical(row[0]) === canonical(size))).forEach(size => {
        const row = document.createElement('tr');
        row.dataset.vrUnmeasured = '';
        const label = document.createElement('th');
        label.scope = 'row';
        label.textContent = displaySize(size, this.kind);
        const note = document.createElement('td');
        note.colSpan = body.closest('table').tHead.rows[0].cells.length - 1;
        note.textContent = this.words.measurements_missing;
        row.append(label, note);
        body.append(row);
      });
      const names = this.kind === 'shoes' ? ['foot', 'width'] : this.kind === 'lower' ? ['waist', 'hip'] : ['chest', 'waist'];
      this.querySelector('[data-vr-first-label]').textContent = this.words[names[0]];
      this.querySelector('[data-vr-second-label]').textContent = this.words[names[1]];
      this.form.elements.first.setAttribute('aria-label', this.words[names[0]] + ' (cm)');
      this.form.elements.second.setAttribute('aria-label', this.words[names[1]] + ' (cm)');
      this.form.elements.first.max = this.kind === 'shoes' ? '40' : '250';
      this.form.elements.second.max = this.kind === 'shoes' ? '20' : '250';
      const footOnly = this.footOnly && this.kind === 'shoes';
      this.form.elements.second.closest('label').hidden = footOnly;
      this.form.elements.second.disabled = footOnly;
      if (footOnly) this.form.elements.second.value = '';
      this.convert(this.unit);
    }

    action(action) {
      if (action === 'close') { this.dialog.close(); return; }
      if (action === 'apply') { this.applySize(); return; }
      if (!this.dialog.open) {
        this.opener = document.activeElement;
        this.dialog.showModal();
      }
      this.querySelector('.vr-size-error').hidden = true;
      this.show(action === 'advice' ? 'advice' : 'guide');
    }

    show(view) {
      this.querySelectorAll('[data-vr-view]').forEach(element => { element.hidden = element.dataset.vrView !== view; });
      this.dialog.scrollTop = 0;
      const heading = this.querySelector(`[data-vr-view="${view}"] h2`);
      if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
    }

    error(key) {
      if (key === 'invalid' || key === 'outside') this.querySelector('[data-vr-measurements]').open = true;
      const element = this.querySelector('.vr-size-error');
      element.textContent = this.words[key];
      element.hidden = false;
      element.tabIndex = -1;
      element.focus();
    }

    recommend() {
      const fieldNames = this.kind === 'shoes' && this.footOnly ? ['first'] : this.kind === 'dress' ? ['first', 'second', 'third'] : ['first', 'second'];
      const raw = fieldNames.map(name => this.form.elements[name].value);
      if (raw.some((value, index) => value !== '' && (!Number.isFinite(Number(value)) || Number(value) <= 0 || !this.form.elements[fieldNames[index]].checkValidity()))) {
        this.error('invalid'); return;
      }
      const measured = raw.some(value => value !== '');
      const values = raw.map(value => value === '' ? null : Number(value));
      const rows = this.tables[this.kind];
      let index = -1;
      if (measured) {
        if (values.some((value, position) => value !== null && (value < Math.min(...bounds(rows[0][position + 1])) || value > high(rows.at(-1)[position + 1])))) {
          this.error('outside'); return;
        }
        index = rows.findIndex(row => values.every((value, position) => value === null || value <= high(row[position + 1])));
        if (index < 0) { this.error('outside'); return; }
      } else {
        if (!this.form.elements.usual.value) { this.error('missing'); return; }
        index = rows.findIndex(row => canonical(row[0]) === canonical(this.form.elements.usual.value));
      }
      let loose = false;
      if (measured && this.form.elements.fit.value === 'relaxed') {
        if (index === rows.length - 1) { this.error('outside'); return; }
        index++; loose = true;
      }
      const actual = this.sizes.find(value => canonical(value) === canonical(measured ? rows[index]?.[0] : this.form.elements.usual.value));
      if (!actual) { this.error('unavailable'); return; }
      this.recommendedSize = actual;
      this.querySelector('[data-vr-result-label]').textContent = this.words[measured ? 'measured_label' : 'estimate_label'];
      const resultSize = this.querySelector('[data-vr-result-size]');
      const half = this.kind === 'shoes' && /^(\d+),5$/.exec(canonical(actual));
      if (half) {
        const fraction = document.createElement('span');
        fraction.className = 'vr-size-fraction';
        fraction.textContent = '½';
        resultSize.replaceChildren(document.createTextNode(half[1]), fraction);
      } else resultSize.textContent = displaySize(actual, this.kind);
      let note = this.words[measured ? 'measured_note' : 'reference_note'];
      if (measured && values.some(value => value === null)) note += ' ' + this.words.partial;
      if (loose) note += ' ' + this.words.fit_note;
      this.querySelector('[data-vr-result-note]').textContent = note;
      this.querySelectorAll('[data-vr-table] tbody tr').forEach(element => element.removeAttribute('data-vr-highlight'));
      Array.from(this.querySelector(`[data-vr-table="${this.kind}"] tbody`).children).find(row => canonical(row.cells[0].textContent) === canonical(actual))?.setAttribute('data-vr-highlight', '');
      this.querySelector('.vr-size-error').hidden = true;
      this.updateApplyButton();
      this.show('result');
    }

    selection() {
      const picker = this.getPicker();
      if (!picker || !this.recommendedSize) return null;
      const selected = new Map();
      const values = [...picker.querySelectorAll('input[type="radio"]:checked, select option:checked')];
      for (const value of values) {
        const position = Number(value.dataset.inputId?.split('-')[0]) - 1;
        if (Number.isInteger(position) && position >= 0) selected.set(position, value.value);
      }
      for (let position = 0; position < (this.variants[0]?.options.length || 0); position++) {
        if (position === this.sizePosition || selected.has(position)) continue;
        const options = [...new Set(this.variants.map(variant => variant.options[position]))];
        if (options.length === 1) selected.set(position, options[0]);
      }
      const variant = this.variants.find(candidate => candidate.available && candidate.options.every((value, position) =>
        position === this.sizePosition ? canonical(value) === canonical(this.recommendedSize) : value === selected.get(position)
      ));
      if (!variant) return null;
      const control = [...picker.querySelectorAll('input[type="radio"][data-input-id], select option[data-input-id]')].find(value =>
        Number(value.dataset.inputId.split('-')[0]) - 1 === this.sizePosition && canonical(value.value) === canonical(this.recommendedSize)
      );
      if (!control) {
        const currentData = picker.querySelector('script[type="application/json"]')?.textContent;
        if (!currentData) return null;
        try {
          const current = JSON.parse(currentData);
          if (current.available && current.options?.length === variant.options.length && current.options.every((value, position) => value === variant.options[position])) return { control: null, variant };
        } catch { return null; }
        return null;
      }
      if (control.disabled || control.getAttribute('aria-disabled') === 'true') return null;
      return { control, variant };
    }

    updateApplyButton() {
      if (!this.recommendedSize) return;
      const available = Boolean(this.selection());
      const button = this.querySelector('[data-vr-action="apply"]');
      button.textContent = this.words.apply + ' ' + displaySize(this.recommendedSize, this.kind);
      button.disabled = !available;
      const status = this.querySelector('[data-vr-apply-status]');
      status.textContent = available ? '' : this.words.selection_unavailable;
      status.hidden = available;
    }

    applySize() {
      const selection = this.selection();
      if (!selection) { this.updateApplyButton(); return; }
      const { control } = selection;
      if (!control) { this.dialog.close(); return; }
      if (control instanceof HTMLOptionElement) {
        const select = control.closest('select');
        if (select.value !== control.value) {
          select.value = control.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.opener = select;
      } else {
        if (!control.checked) control.click();
        this.opener = control;
      }
      this.dialog.close();
    }

    convert(unit) {
      this.unit = unit;
      this.querySelectorAll('[data-vr-cm]').forEach(element => {
        element.textContent = unit === 'cm' ? element.dataset.vrCm : element.dataset.vrCm.replace(/\d+(?:\.\d+)?/g, number => (Number(number) / 2.54).toFixed(1));
      });
      this.querySelectorAll('[data-vr-unit]').forEach(element => { element.textContent = unit; });
      this.querySelectorAll('[data-vr-unit-choice]').forEach(element => element.setAttribute('aria-pressed', String(element.dataset.vrUnitChoice === unit)));
    }
  }
  customElements.define('vr-size-guide', VrSizeGuide);
})();

(() => {
  const start = root => {
    if(root.dataset.vrBadgeAligned) return;
    root.dataset.vrBadgeAligned='true';
    const observed=new WeakSet();
    let queued=false;
    const align=gallery=>{
      const badge=gallery.querySelector(':scope > .vr-savings-badge');
      if(!badge) return;
      const frame=gallery.getBoundingClientRect();
      if(!frame.width||!frame.height) return;
      const images=Array.from(gallery.querySelectorAll('.product-media__image'));
      const image=images.find(img=>{const r=img.getBoundingClientRect();return r.width>0&&r.left<frame.right&&r.right>frame.left;});
      if(!image||!image.naturalWidth||!image.naturalHeight) return;
      const r=image.getBoundingClientRect();
      const contain=getComputedStyle(image).objectFit==='contain';
      const scale=Math.min(r.width/image.naturalWidth,r.height/image.naturalHeight);
      const width=contain?image.naturalWidth*scale:r.width;
      const height=contain?image.naturalHeight*scale:r.height;
      gallery.style.setProperty('--vr-badge-photo-left',Math.max(6,r.left-frame.left+(r.width-width)/2+6)+'px');
      gallery.style.setProperty('--vr-badge-photo-top',Math.max(6,r.top-frame.top+(r.height-height)/2+6)+'px');
    };
    const resize=new ResizeObserver(entries=>entries.forEach(e=>align(e.target)));
    const scan=()=>{
      queued=false;
      root.querySelectorAll('.card-gallery').forEach(gallery=>{
        if(!observed.has(gallery)){observed.add(gallery);resize.observe(gallery);}
        align(gallery);
      });
    };
    const schedule=()=>{if(!queued){queued=true;requestAnimationFrame(scan);}};
    new MutationObserver(schedule).observe(root,{childList:true,subtree:true});
    root.addEventListener('load',schedule,true);
    root.addEventListener('scroll',schedule,true);
    scan();
  };
  document.querySelectorAll('.product-recommendations').forEach(start);
  document.addEventListener('shopify:section:load',event=>{
    event.target.querySelectorAll('.product-recommendations').forEach(start);
    if(event.target.matches('.product-recommendations')) start(event.target);
  });
})();




